import { SEOHead } from "@/components/SEOHead";
import { Layout } from "@/components/layout/Layout";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

const NoQ5VsAgentforce = () => {
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "NoQ5 vs Salesforce Agentforce — CRM AI Agent Comparison 2026",
      "url": "https://noq5.ai/noq5-vs-agentforce"
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://noq5.ai" },
        { "@type": "ListItem", "position": 2, "name": "Comparisons", "item": "https://noq5.ai/comparison" },
        { "@type": "ListItem", "position": 3, "name": "NoQ5 vs Agentforce", "item": "https://noq5.ai/noq5-vs-agentforce" }
      ]
    }
  ];

  const comparison = [
    { feature: "Works with HubSpot & Pipedrive", noq5: "✅", agentforce: "❌ Salesforce only" },
    { feature: "Meeting transcription + analysis", noq5: "✅ Built-in", agentforce: "❌ Requires Einstein Conversation Insights add-on" },
    { feature: "Cross-platform activity capture", noq5: "✅ Email, Slack, Zoom, LinkedIn, Calendar", agentforce: "⚠️ Limited to Salesforce ecosystem" },
    { feature: "Setup time", noq5: "15 minutes", agentforce: "Weeks to months (admin + dev required)" },
    { feature: "Autonomous CRM updates", noq5: "✅ 40+ fields from conversations", agentforce: "⚠️ Rule-based actions, limited autonomy" },
    { feature: "Pipeline forecasting", noq5: "✅ Evidence-based from engagement data", agentforce: "⚠️ Relies on rep-submitted data" },
    { feature: "Deal risk detection", noq5: "✅ Proactive alerts within 48 hours", agentforce: "⚠️ Basic signals only" },
    { feature: "Custom field support", noq5: "✅ Auto-detects schema", agentforce: "✅ Full Salesforce schema" },
    { feature: "Pricing", noq5: "Per-seat, all features included", agentforce: "$2/conversation + Einstein license + platform fees" },
    { feature: "No-code setup", noq5: "✅ Zero configuration", agentforce: "❌ Requires Salesforce admin expertise" },
    { feature: "GDPR / EU data residency", noq5: "✅ EU data centers", agentforce: "⚠️ Depends on Salesforce instance region" },
    { feature: "Contact enrichment", noq5: "✅ 30+ fields from 15+ sources", agentforce: "❌ Not included" },
  ];

  return (
    <Layout>
      <SEOHead
        title="NoQ5 vs Salesforce Agentforce — CRM Agent Comparison 2026"
        description="NoQ5 vs Salesforce Agentforce: which CRM AI agent is better? Compare autonomous CRM updates, meeting intelligence, forecasting, pricing, and setup time."
        keywords="NoQ5 vs Agentforce, Salesforce Agentforce alternative, Agentforce competitor, Agentforce comparison, CRM agent vs Agentforce, Salesforce AI agent alternative, best Salesforce AI agent, Agentforce limitations, Agentforce pricing"
        canonicalUrl="https://noq5.ai/noq5-vs-agentforce"
        jsonLd={jsonLd}
      />

      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-primary/5" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <nav className="text-sm text-muted-foreground mb-6">
              <Link to="/" className="hover:text-primary">Home</Link> / <Link to="/comparison" className="hover:text-primary">Comparisons</Link> / <span className="text-foreground">NoQ5 vs Agentforce</span>
            </nav>
            <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6 leading-tight">
              <span className="text-primary">NoQ5</span> vs Salesforce Agentforce
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              Agentforce is Salesforce's native AI agent. But it only works within Salesforce, requires admin expertise to configure, charges per conversation, and can't capture activities from your other tools. Here's the full comparison.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-3xl font-bold text-foreground mb-8 text-center">Complete Feature Comparison</h2>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse bg-card rounded-xl overflow-hidden border border-border text-sm">
              <thead>
                <tr className="bg-muted">
                  <th className="text-left p-3 font-semibold text-foreground">Capability</th>
                  <th className="p-3 font-semibold text-primary text-center">NoQ5</th>
                  <th className="p-3 font-semibold text-foreground text-center">Salesforce Agentforce</th>
                </tr>
              </thead>
              <tbody>
                {comparison.map((row) => (
                  <tr key={row.feature} className="border-t border-border">
                    <td className="p-3 font-medium text-foreground">{row.feature}</td>
                    <td className="p-3 text-center text-sm bg-primary/5 text-foreground">{row.noq5}</td>
                    <td className="p-3 text-center text-sm text-muted-foreground">{row.agentforce}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-muted/20">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="prose prose-lg max-w-none text-muted-foreground space-y-6">
            <h2 className="text-3xl font-bold text-foreground">The 5 Reasons Teams Switch from Agentforce to NoQ5</h2>
            
            <h3 className="text-2xl font-bold text-foreground">1. Agentforce Only Works Inside Salesforce</h3>
            <p>Your reps use Gmail, Slack, Zoom, LinkedIn, and calendar apps every day. Agentforce can't see any of that natively. NoQ5 captures activities from all these channels and syncs them to Salesforce — giving you a complete picture of every deal, not just what's logged in Salesforce itself.</p>

            <h3 className="text-2xl font-bold text-foreground">2. Agentforce Requires Significant Admin Configuration</h3>
            <p>Building Agentforce agents requires Salesforce admin expertise, prompt engineering, and ongoing maintenance. NoQ5 connects via OAuth in 15 minutes and starts working immediately — no admin, no developer, no configuration.</p>

            <h3 className="text-2xl font-bold text-foreground">3. Agentforce Charges Per Conversation</h3>
            <p>At $2 per conversation, costs scale unpredictably as usage grows. A team of 20 reps having 50 interactions/day could face $2,000/day in Agentforce costs alone. NoQ5 uses simple per-seat pricing with unlimited agent operations.</p>

            <h3 className="text-2xl font-bold text-foreground">4. No Real Meeting Intelligence</h3>
            <p>Agentforce doesn't transcribe and analyze sales calls natively. Einstein Conversation Insights is a separate product with additional licensing. NoQ5 includes full meeting intelligence — transcription, context extraction, action item detection, and automatic CRM updates — in every plan.</p>

            <h3 className="text-2xl font-bold text-foreground">5. Salesforce Lock-In</h3>
            <p>If you ever switch CRMs or need to support a team using HubSpot or Pipedrive alongside Salesforce, Agentforce can't help. NoQ5 works across all three major CRMs, making it the right choice for multi-CRM environments and future flexibility.</p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-primary/5">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Get Everything Agentforce Promises — Plus More
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            NoQ5 gives you autonomous CRM updates, meeting intelligence, pipeline forecasting, and cross-platform activity capture — in 15 minutes, at a fraction of the cost.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/trial"><Button size="lg" className="text-lg px-8 py-6 bg-primary hover:bg-primary/90 text-primary-foreground">Try NoQ5 Free <ArrowRight className="ml-2 h-5 w-5" /></Button></Link>
            <Link to="/crm-agent"><Button size="lg" variant="outline" className="text-lg px-8 py-6 border-border text-foreground hover:bg-accent">How Our CRM Agent Works</Button></Link>
          </div>
        </div>
      </section>

      <section className="py-12 border-t border-border">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-xl font-semibold text-foreground mb-6">More Comparisons</h2>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
            <Link to="/noq5-vs-relevance-ai" className="text-primary hover:underline text-sm">NoQ5 vs Relevance AI</Link>
            <Link to="/noq5-vs-hubspot-ai" className="text-primary hover:underline text-sm">NoQ5 vs HubSpot AI</Link>
            <Link to="/comparison/salesforce-einstein" className="text-primary hover:underline text-sm">NoQ5 vs Salesforce Einstein</Link>
            <Link to="/crm-agent" className="text-primary hover:underline text-sm">CRM AI Agent</Link>
            <Link to="/ai-agent-salesforce" className="text-primary hover:underline text-sm">AI Agent for Salesforce</Link>
            <Link to="/comparison" className="text-primary hover:underline text-sm">All Comparisons</Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default NoQ5VsAgentforce;
